/**
 * Governance — who can do what, resolved from real project memberships.
 *
 * The frontend twin of the backend authorization layer:
 *   server/compliance/rolepacks/zeus-cascade-1.0.0.js  (the rulebook)
 *   server/helpers/approvalRoles.js                    (getProjectPermissions / assertCanAct)
 *
 * In demo mode the profile switcher drives identity and these hooks answer
 * permission questions from `WorkspaceSeed.memberships`. In real-auth mode
 * they're fed from `GET /project-membership/project/:projectId` (per project,
 * filtered to the active user) — the "Phase B" gap this file used to note is
 * closed; see useMyMemberships below.
 *
 * ROLE_PERMISSIONS mirrors the server pack verbatim — if the pack changes,
 * change it there first and copy here.
 */

import { useQuery } from "@tanstack/react-query";
import type { CascadeRole, DemoMembership, Project, SpendRequest } from "./types";
import { useActiveProfile, useSeed, useVisibleProjects, useProjects } from "./hooks";
import { isAboveStratusThreshold } from "./spendThreshold";
import { api, demoModeActive, type ProjectMemberRow } from "./api";
import { fetchWithOfflineCache } from "./offlineDb";

export const ROLE_PERMISSIONS: Record<CascadeRole, readonly string[]> = {
  stratus_admin: [
    "manage_programme",
    "manage_contractors",
    "approve_milestone",
    "route_to_funder",
    "view_all",
    "leak_investigate",
    "configure_compliance_pack",
    "configure_approval_gates",
    "send_invites",
  ],
  funder_approver: [
    "view_funder_summary",
    "approve_disbursement",
    "comment_to_funder",
    "view_audit_log",
  ],
  prime_lead: [
    "manage_project",
    "approve_spend_ask",
    "submit_milestone_evidence",
    "invite_team",
    "view_project_team",
  ],
  prime_member: ["view_project", "raise_spend_ask", "submit_evidence"],
  sub_lead: [
    "manage_subproject",
    "approve_spend_ask_scoped",
    "submit_evidence_scoped",
    "invite_team_scoped",
    "view_subproject_team",
  ],
  sub_member: ["view_subproject", "raise_spend_ask_scoped", "submit_evidence_scoped"],
  reporter: ["view_assignment", "raise_spend_ask", "submit_evidence"],
};

export type GovernanceScope = { projectId?: string; subProjectId?: string };

/**
 * Whether a membership row applies to a scope, mirroring the backend's
 * membership resolution:
 *  - programme-wide rows (no projectId) apply everywhere;
 *  - project rows apply to that project;
 *  - sub-project-scoped rows apply ONLY to their own sub-project — a sub lead's
 *    authority never reaches project-level asks or sibling sub-projects.
 */
function membershipCovers(m: DemoMembership, scope: GovernanceScope): boolean {
  if (!m.projectId) return true;
  if (m.projectId !== scope.projectId) return false;
  if (m.subProjectId) return m.subProjectId === scope.subProjectId;
  return true;
}

/**
 * Pure selection step of useMyMemberships' real-mode path, pulled out so it's
 * testable without a QueryClient/provider tree: per project, keep only the
 * requesting user's own active row and map it to the DemoMembership shape
 * useCan()/useRaisableProjects() already understand.
 */
export function deriveMyMembershipsFromRows(
  rows: { projectId: string; members: ProjectMemberRow[] }[],
  personId: string,
): DemoMembership[] {
  const out: DemoMembership[] = [];
  for (const { projectId, members } of rows) {
    for (const m of members) {
      if (m.status === "active" && String(m.userId) === personId) {
        out.push({ personId, role: m.role as CascadeRole, projectId });
      }
    }
  }
  return out;
}

/**
 * The active profile's membership rows, or null when there's nothing to
 * resolve from yet (a workspace with no seed memberships in demo mode; the
 * real-mode query hasn't returned yet, or the active profile holds no
 * project memberships at all) — callers treat null as "no governance data,
 * fall back to permissive default" (see useCan below).
 *
 * Demo mode reads `WorkspaceSeed.memberships` exactly as before. Real mode
 * has no single "my memberships" endpoint, so this fetches per-project
 * membership rows the same way ApprovalQueueTab.tsx's dbThresholdAsks
 * already aggregates cross-project data (Promise.allSettled over the real
 * project list), keeping only the row for the active user
 * (`profile.personId` is the real numeric user id, stringified — see
 * authStore.ts) with `status === "active"`. `getProjectMembers` returns the
 * real cascade `role` (prime_lead/sub_lead/etc.), matching
 * ROLE_PERMISSIONS's keys directly — deliberately not the legacy
 * `GET /project-membership` route, which synthesizes a fake "lead"/
 * "reporter" role from a `projectlead` boolean and doesn't share this
 * vocabulary.
 *
 * Iterates useProjects() (real, API-backed numeric ids when available), NOT
 * seed.projects directly - seed is always the demo/fixture blob (GET
 * /api/workspace-seed, hydrated regardless of mode), whose project ids are
 * fake slugs a real getProjectMembers(id) call would always reject. Reading
 * seed.projects here meant this hook silently returned no real memberships
 * for any real user, ever, which is exactly the "Phase B" gap this file's
 * header comment above already describes as closed - it wasn't, until now.
 *
 * Known, deliberate simplification: while the real-mode query is loading,
 * this returns null (the permissive fallback) rather than a distinct
 * "loading" state, so a raise-ask dropdown briefly shows every project
 * before narrowing to the user's own once the fetch resolves — an
 * acceptable transient, not a silent gap; a true loading tri-state would
 * mean threading loading through every useCan()/useRaisableProjects() caller.
 */
export function useMyMemberships(): DemoMembership[] | null {
  const profile = useActiveProfile();
  const seed = useSeed();
  const demo = demoModeActive();
  const realProjects = useProjects();
  const projectIds = realProjects.map((p) => p.id);

  const { data } = useQuery({
    queryKey: ["my-project-memberships", profile.personId, projectIds],
    queryFn: () =>
      fetchWithOfflineCache(`my-project-memberships:${profile.personId}`, async () => {
        const results = await Promise.allSettled(
          realProjects.map((p) => api.getProjectMembers(p.id).then((members) => ({ projectId: p.id, members }))),
        );
        const rows = results
          .filter((r): r is PromiseFulfilledResult<{ projectId: string; members: ProjectMemberRow[] }> => r.status === "fulfilled")
          .map((r) => r.value);
        return deriveMyMembershipsFromRows(rows, profile.personId);
      }),
    enabled: !demo && projectIds.length > 0,
    networkMode: "always",
  });

  if (demo) {
    const all = seed?.memberships;
    return all ? all.filter((m) => m.personId === profile.personId) : null;
  }
  return data ?? null;
}

/**
 * Core permission check. Returns:
 *  - true/false when the workspace has membership data for this person;
 *  - null when governance data is absent (real-auth identity, or a workspace
 *    without memberships) — callers fall back to pre-governance behaviour.
 * The platform super-admin profile always resolves true.
 */
export function useCan(): (permission: string, scope: GovernanceScope) => boolean | null {
  const profile = useActiveProfile();
  const memberships = useMyMemberships();
  return (permission, scope) => {
    if (profile.permissions.includes("platform.superadmin")) return true;
    if (memberships === null || memberships.length === 0) return null;
    return memberships.some(
      (m) => membershipCovers(m, scope) && ROLE_PERMISSIONS[m.role].includes(permission),
    );
  };
}

/**
 * ZEUS-127 — whether the active profile may create/edit/toggle the status of
 * a ZEUS project (backend: `create_project`/`edit_project_direct`/
 * `toggle_project_status`, all gated to stratus_admin's `manage_programme`
 * permission — see server/helpers/approvalRoles.js). No project view
 * consumes this yet (ZEUS-132/133 build the actual Create/Edit UI); this
 * hook exists so those stories gate their controls the same way every other
 * permission check in this file does, rather than re-deriving the role
 * check inline. Mirrors `useCan()`'s null-when-absent fallback.
 */
export function useCanManageProjectGovernance(): boolean | null {
  const can = useCan();
  return can("manage_programme", {});
}

/**
 * Permissions the SpendAskDrawer footer needs for one ask:
 *  - mayApprove — may approve/query/reject a proposed ask (the contractor lead:
 *    prime_lead, or sub_lead on their own sub-project);
 *  - mayConfirm — confirm/send back submitted evidence (Stratus oversight —
 *    separation of duties from the lead who approved the spend);
 *  - awaitingVerification — the ask is above threshold and Stratus has not yet marked
 *    it independently verified, so no one may approve it yet: Stratus verifies first
 *    (the "Mark independently verified" action), then it routes to the contractor lead.
 *    The drawer uses this to explain why there is no approve action.
 *
 * Above-threshold gate (spec §6.3 / §8.2): an above-threshold ask is routed to Stratus,
 * who must verify it before the contractor lead can approve. Below-threshold asks are
 * unchanged — the contractor lead approves directly.
 *
 * mayApprove/mayConfirm default true when governance data is absent, so the existing
 * `canApprove` prop keeps sole control outside the ZEUS demo workspace.
 */
export function useSpendAskPermissions(): (ask: SpendRequest) => {
  mayApprove: boolean;
  mayConfirm: boolean;
  awaitingVerification: boolean;
} {
  const can = useCan();
  return (ask) => {
    const scope = { projectId: ask.projectId, subProjectId: ask.subProjectId };
    const contractor = can("approve_spend_ask", scope);
    // null means "no governance data" — for one ask it is null for every
    // permission alike, so checking `contractor` alone decides the fallback.
    if (contractor === null) return { mayApprove: true, mayConfirm: true, awaitingVerification: false };

    const isContractor = contractor || (can("approve_spend_ask_scoped", scope) ?? false);
    const isStratus = can("approve_milestone", scope) ?? false;
    // Above threshold and not yet verified by Stratus → the approval gate is closed for
    // everyone; the next action is Stratus's "Mark independently verified", not an approval.
    const awaitingVerification = isAboveStratusThreshold(ask) && !ask.verifiedAt;

    return {
      mayApprove: awaitingVerification ? false : isContractor,
      mayConfirm: isStratus,
      awaitingVerification,
    };
  };
}

/**
 * Projects the active profile may raise a spend ask in.
 * Reporters/members qualify via raise permissions; leads qualify on the scope
 * they manage (spec §1 lets leads raise org asks even though the v1.0.0 pack
 * gives lead roles no raise permission — flagged for a pack revision).
 * Falls back to the org-based `useVisibleProjects()` when no membership data
 * exists (real-auth mode, non-ZEUS workspaces).
 */
const RAISE_PERMISSIONS = [
  "raise_spend_ask",
  "raise_spend_ask_scoped",
  "manage_project",
  "manage_subproject",
];

/**
 * Pure filtering step of useRaisableProjects, pulled out so it's testable
 * without a QueryClient/provider tree — mirrors deriveMyMembershipsFromRows
 * above. `visibleProjects` (useVisibleProjects → useProjects), not
 * seed.projects: seed is always the demo/fixture blob (GET
 * /api/workspace-seed, hydrated regardless of mode), whose ids are fake
 * slugs that never match a real membership row's numeric projectId -
 * filtering against it left real reporters/members with an always-empty
 * raise dropdown, same root cause as useMyMemberships above.
 */
export function selectRaisableProjects(
  memberships: DemoMembership[],
  visibleProjects: Project[],
): Project[] {
  const raisable = memberships.filter((m) =>
    RAISE_PERMISSIONS.some((p) => ROLE_PERMISSIONS[m.role].includes(p)),
  );
  if (raisable.some((m) => !m.projectId)) return visibleProjects;
  const ids = new Set(raisable.map((m) => m.projectId));
  return visibleProjects.filter((p) => ids.has(p.id));
}

export function useRaisableProjects(): Project[] {
  const memberships = useMyMemberships();
  const visible = useVisibleProjects();
  if (memberships === null || memberships.length === 0) return visible;
  return selectRaisableProjects(memberships, visible);
}
